import { Field, Formik, Form } from "formik";
import { capitalize } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { object, ref, string } from "yup";

import { PasswordResetConfirm } from "pwapi";

import {
  confirm_reset_password,
  wipe_password_confirm_reset,
} from "shared/actions/authActions";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { ApplicationState } from "app/store";
import LoginLayout from "ui/components/LoginLayout";
import PasswordEyeButton from "ui/components/PasswordEyeButton";

const ChangePasswordPage = () => {
  const dispatch = useAppDispatch();
  const { detail, passwordIssues } = useAppSelector(
    ({ passwordResetConfirm }: ApplicationState) => passwordResetConfirm
  );
  const navigate = useNavigate();

  const schema = object().shape({
    new_password1: string()
      .min(8)
      .required("Password required")
      .label("Password"),
    new_password2: string()
      .min(8)
      .required("Password confirmation required")
      .label("Repeat Password")
      .oneOf([ref("new_password1"), undefined], "Passwords must match")
      .label("Repeat Password"),
  });

  const { uid, token } = useParams<{ uid: string; token: string }>();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    dispatch(wipe_password_confirm_reset());
  }, []);

  return (
    <LoginLayout>
      <div className="LoginPage">
        <Formik
          initialValues={{
            new_password1: "",
            new_password2: "",
            uid: uid,
            token: token,
          }}
          validationSchema={schema}
          validateOnChange={false}
          validateOnBlur={false}
          validate={() =>
            (detail || passwordIssues.length) &&
            dispatch(wipe_password_confirm_reset())
          }
          onSubmit={(values, { setSubmitting }) => {
            dispatch(
              confirm_reset_password(values as PasswordResetConfirm, navigate)
            );
            setSubmitting(false);
          }}
        >
          {({ isSubmitting, errors }) => (
            <Form translate="yes" noValidate>
              <div className="message_container">
                <h1>Reset Your Password</h1>
                <h2>Password Requirements</h2>
                <span className="message_group">
                  Your password can’t be too similar to your other personal
                  information.
                  <br />
                  Your password must contain at least 8 characters.
                  <br />
                  Your password can't be a commonly used password.
                  <br />
                  Your password can't be entirely numeric.
                </span>
                {(!!(passwordIssues?.length > 0) ||
                  errors.new_password1 ||
                  errors.new_password2) && (
                  <div className="alert alert-danger">
                    <>
                      {!!passwordIssues.length &&
                        passwordIssues.map((err: string) => (
                          <span key={err}>{err}</span>
                        ))}
                      {errors.new_password1 && (
                        <span>{capitalize(errors.new_password1)}</span>
                      )}
                      {errors.new_password2 && (
                        <span>{capitalize(errors.new_password2)}</span>
                      )}
                    </>
                  </div>
                )}
              </div>
              <div className="LoginForm__password">
                <Field
                  type={showPassword ? "text" : "password"}
                  name="new_password1"
                  id="new_password1"
                  data-testid="new_password1"
                  placeholder="Enter new password"
                  required
                />
                <PasswordEyeButton
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                />
              </div>
              <div className="LoginForm__password">
                <Field
                  type={showConfirmPassword ? "text" : "password"}
                  name="new_password2"
                  id="new_password2"
                  data-testid="new_password2"
                  placeholder="Confirm password"
                  required
                />
                <PasswordEyeButton
                  showPassword={showConfirmPassword}
                  setShowPassword={setShowConfirmPassword}
                />
              </div>
              <div className="mfa-form-buttons">
                <button
                  type="submit"
                  title="Reset Password"
                  className="Button"
                  style={{ whiteSpace: "nowrap", width: "auto" }}
                  disabled={isSubmitting}
                >
                  Reset Password
                </button>
                <button
                  type="button"
                  title="Back to Login"
                  className="Button--text"
                  style={{ whiteSpace: "nowrap", width: "auto" }}
                  disabled={isSubmitting}
                  onClick={() => navigate("/", { replace: true })}
                >
                  Back to Login
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </LoginLayout>
  );
};

export default ChangePasswordPage;
